import type {
  DsdEditorNoteReferenceCheck,
  DsdEditorValueMatch,
} from "@/apis/dsd-editor/route";
import type { PlainDsdNoteReferenceStatus } from "@/types/plain-dsd-table";
import type { DsdEditorSelectedCellSummary } from "@/utils/dsd-editor/document-items";
import { getOccurrenceIds } from "@/utils/dsd-editor/note-reference-review";
import {
  buildDsdReviewSelectedCellView,
  type DsdReviewMatchItem,
} from "./panel/selected-cell-view";

/** 톤 → 셀 상태. 세 값 다 PlainDsdNoteReferenceStatus 에 있어 색·배너가 그대로 붙는다 */
const STATUS_OF_TONE: Record<
  DsdReviewMatchItem["tone"],
  PlainDsdNoteReferenceStatus
> = {
  negative: "reference_missing",
  caution: "unconfirmed",
  positive: "confirmed",
};
const TONE_RANK: Record<DsdReviewMatchItem["tone"], number> = {
  positive: 0,
  caution: 1,
  negative: 2,
};

/**
 * 본문 셀 색은 그 셀의 후보 카드에서 정한다 — 셀 색을 정하는 곳은 여기 하나다. 빨강이 하나라도 있으면 빨강,
 * 없고 노랑이 있으면 노랑, 그 외 초록. "문제 없는 항목" 으로 판단한 카드는 빼고 세므로 검토가 색을 바꾼다
 * ("추가 확인 필요" 는 아직 볼 게 남은 것이라 원래 톤 유지).
 * - 재무제표 본문 안 후보 카드는 일치 케이스라 늘 초록이다(selected-cell-view) — 본문에만 후보가 있는 셀은 초록
 * - 카드가 하나도 없는 셀은 상태 그대로(연결 없음이면 무색)
 *
 * 결과는 **색 전용**이다(초록/노랑/빨강 셋). 배너 문구·상태 라벨은 백엔드 상태를 그대로 써야 한다 —
 * 조립은 이 맵을 셀·후보 색과 배너 톤에만 넣는다.
 * 저장할 때마다 다시 계산되므로 셀마다 전체 배열을 훑지 않도록 cellId 인덱스를 먼저 만든다.
 */
export const deriveDsdReviewStatusByCellId = ({
  backendStatusByCellId,
  noteReferenceChecks,
  valueMatches,
  cellSummaryById,
}: {
  backendStatusByCellId: ReadonlyMap<string, PlainDsdNoteReferenceStatus>;
  noteReferenceChecks: DsdEditorNoteReferenceCheck[];
  valueMatches: DsdEditorValueMatch[];
  cellSummaryById: ReadonlyMap<string, DsdEditorSelectedCellSummary>;
}): ReadonlyMap<string, PlainDsdNoteReferenceStatus> => {
  const matchByCellId = new Map<string, DsdEditorValueMatch>();
  for (const match of valueMatches)
    for (const id of getOccurrenceIds(match.source))
      if (!matchByCellId.has(id)) matchByCellId.set(id, match);
  const checksByCellId = new Map<string, DsdEditorNoteReferenceCheck[]>();
  for (const check of noteReferenceChecks)
    for (const id of getOccurrenceIds(check.source))
      checksByCellId.set(id, [...(checksByCellId.get(id) ?? []), check]);

  const map = new Map<string, PlainDsdNoteReferenceStatus>();
  for (const [cellId, backendStatus] of backendStatusByCellId) {
    const view = buildDsdReviewSelectedCellView({
      selectedCellId: cellId,
      cellSummaryById,
      activeValueMatch: matchByCellId.get(cellId) ?? null,
      statusByCellId: backendStatusByCellId,
      noteReferenceChecks: checksByCellId.get(cellId) ?? [],
    });
    const items = view?.items ?? [];
    if (items.length === 0) {
      map.set(cellId, backendStatus);
      continue;
    }
    const open = items.filter((item) => item.review?.judgement !== "no_issue");
    const worst = open.reduce<DsdReviewMatchItem["tone"]>(
      (acc, item) => (TONE_RANK[item.tone] > TONE_RANK[acc] ? item.tone : acc),
      "positive",
    );
    map.set(cellId, STATUS_OF_TONE[worst]);
  }
  return map;
};
